import template from './ko-field-view-multiselect-list.html';

class MultiSelectListViewComponentVM
{
	constructor(params)
	{
		this.params = params;
		this.value = ko_helper.safe_observable(params.value);
		this.extra_classes = ko_helper.safe_observableArray(params.extra_classes || []);
		this.options = ko.observableArray([]);
		this.optionsText = ko_helper.safe_observable(params.options_text || params.list_label_field, null);
		this.optionsValue = ko_helper.safe_observable(params.options_value || params.list_value_field, null);

		// Create a computed observable that gets the display text for the selected values
		this.displayValues = ko.computed(() => {
			const values = this.value();
			if (!values || !Array.isArray(values) || values.length === 0) return [];

			// If we have options, find the matching ones
			const options = this.options();
			if (options.length > 0)
			{
				return values.map(value => {
					let matchingOption;

					if (this.optionsValue())
						matchingOption = options.find(opt => opt[this.optionsValue()] === value);
					else if (typeof value === 'object')
					{
						// For object values (like from cache), try to match the whole object
						matchingOption = options.find(opt => {
							if (this.optionsValue())
								return opt[this.optionsValue()] === value[this.optionsValue()];

							return JSON.stringify(opt) === JSON.stringify(value);
						});
					}
					else
						matchingOption = options.find(opt => opt === value);

					if (matchingOption)
					{
						// If we have a text field specified, use it
						if (this.optionsText())
							return matchingOption[this.optionsText()];

						if (typeof matchingOption === 'object')
						{
							const keys = Object.keys(matchingOption);
							const stringKey = keys.find(k => typeof matchingOption[k] === 'string' || typeof matchingOption[k] === 'number');
							return stringKey ? matchingOption[stringKey] : JSON.stringify(matchingOption);
						}

						// Fallback to string representation
						return String(matchingOption);
					}

					// If no matching option found, try to display the value directly
					if (typeof value === 'object')
					{
						// For object values, try to find a suitable display field
						if (this.optionsText() && value[this.optionsText()])
							return value[this.optionsText()];

						const keys = Object.keys(value);
						const stringKey = keys.find(k => typeof value[k] === 'string' || typeof value[k] === 'number');
						return stringKey ? value[stringKey] : JSON.stringify(value);
					}

					// Fallback to string representation
					return String(value);
				});
			}

			// If no options, convert values to strings
			return values.map(value => {
				if (typeof value === 'object')
				{
					if (this.optionsText() && value[this.optionsText()])
						return value[this.optionsText()];

					const keys = Object.keys(value);
					const stringKey = keys.find(k => typeof value[k] === 'string' || typeof value[k] === 'number');
					return stringKey ? value[stringKey] : JSON.stringify(value);
				}
				return String(value);
			});
		});

		this.load_options();
	}

	async load_options()
	{
		let rawItems = [];
		let options = this.params.options || this.params.ui_fields?.options;

		// 1. Items from cache
		if (this.params.items_from_cache || this.params.ui_fields?.items_from_cache)
		{
			if (this.params.items_from_cache == 'AllCaches')
			{
				let x = Grape.cache.cache_items || {};
				rawItems = Object.keys(x);
				this.optionsText('text');
				this.optionsValue('value');
			}
			else
			{
				let cache;
				if (this.params.items_from_cache)
					cache = this.params.items_from_cache;
				else
					cache = this.params.ui_fields.items_from_cache;

				try {
					rawItems = await Grape.cache.get(cache);

					// Ensure proper text/value fields for cache items
					if (Array.isArray(rawItems) && rawItems.length > 0)
					{
						let first = rawItems[0];
						if (typeof first === 'string' || typeof first === 'number')
						{
							rawItems = rawItems.map(i => ({ text: String(i), value: i }));
							this.optionsText('text');
							this.optionsValue('value');
						}
						else if (typeof first === 'object')
						{
							// Check if name/value fields
							if (first.hasOwnProperty('name') && !this.optionsText())
								this.optionsText('name');

							// Only set optionsValue if specified in params
							if (this.params.options_value || this.params.list_value_field)
							{
								if (first.hasOwnProperty('value'))
									this.optionsValue('value');
							}
							// If no optionsText, find a suitable display field
							if (!this.optionsText())
							{
								let keys = Object.keys(first);
								let stringKey = keys.find(k => typeof first[k] === 'string' || typeof first[k] === 'number');
								if (stringKey)
									this.optionsText(stringKey);
							}
						}
					}
				} catch (err) {
					console.error('Cache error:', err);
					rawItems = [];
				}
			}
		}
		// 2. Lookup
		else if (this.params.lookup || this.params.ui_fields?.lookup)
		{
			let res = await Grape.fetches.getJSON('/api/record', {
				schema: 'grape',
				table: 'v_lookup_value',
				filter: [{ field: 'lookup_name', operand: '=', value: this.params.lookup }]
			});

			rawItems = res.records || [];
			if (!this.optionsText())  this.optionsText('name');
			if (!this.optionsValue()) this.optionsValue('lookup_value_id');
		}
		// 3. Custom fetch function
		else if (this.params.fetch_items instanceof Function)
			rawItems = await this.params.fetch_items(this) || [];

		// 4. Manual items (only if no cache/lookup/fetch)
		else if (this.params.items)
		{
			if (typeof this.params.items === 'string')
			{
				try {
					// Handle string that looks like an array (e.g. "['Yes', 'No']")
					if (this.params.items.trim().startsWith('[') && this.params.items.trim().endsWith(']'))
					{
						// Remove the outer brackets and split by comma
						let content = this.params.items.trim().slice(1, -1);
						rawItems = content.split(',').map(item => 
							item.trim().replace(/^['"]|['"]$/g, '')
						);
					}
					else
					{
						// Try regular JSON parse first
						try {
							rawItems = JSON.parse(this.params.items);
						} catch {
							// If that fails, treat as comma-separated
							rawItems = this.params.items.split(',').map(item => item.trim().replace(/^['"]|['"]$/g, ''));
						}
					}
				} catch (err) {
					console.error('Invalid manual items:', err);
					rawItems = [];
				}
			}
			else if (Array.isArray(this.params.items))
				rawItems = this.params.items;
		}
		// 5. Legacy manual options (only if no other source)
		else if (options)
		{
			if (typeof options === 'string')
			{
				try {
					// Handle string that looks like an array
					if (options.trim().startsWith('[') && options.trim().endsWith(']'))
					{
						// Remove the outer brackets and split by comma
						let content = options.trim().slice(1, -1);
						rawItems = content.split(',').map(item => 
							item.trim().replace(/^['"]|['"]$/g, '')
						);
					}
					else
					{
						// Try regular JSON parse
						try {
							rawItems = JSON.parse(options);
						} catch {
							rawItems = options.split(',').map(item => item.trim().replace(/^['"]|['"]$/g, ''));
						}
					}
				} catch (err) {
					console.error('Invalid manual options:', err);
					rawItems = [];
				}
			} else if (Array.isArray(options)) {
				rawItems = options;
			}
		}

		// Normalize items array
		let items = [];
		let first = rawItems[0];

		if (typeof first === 'string' || typeof first === 'number')
		{
			items = rawItems.map(i => ({ text: String(i), value: i }));
			this.optionsText('text');
			this.optionsValue('value');
		}
		else if (first && typeof first === 'object')
		{
			items = rawItems;
			if (!this.optionsText())  this.optionsText('name');
			// Don't set default optionsValue 
		}

		this.options(items);
	}
}

export default {
	name: 'ko-field-view-multiselect-list',
	viewModel: MultiSelectListViewComponentVM,
	module_type: 'ko',
	template: template
} 